(() => {
  if (customElements.get('order-tracker')) return;

  const statuses = new Set(['received', 'processing', 'processed', 'awaiting_supplier', 'at_warehouse', 'awaiting_collection', 'dispatched', 'fulfilled', 'in_transit', 'out_for_delivery', 'delivered', 'on_hold', 'cancelled', 'returned_or_refunded', 'update_unavailable']);
  const errorCodes = { 400: 'INVALID_INPUT', 403: 'INVALID_REQUEST', 404: 'ORDER_NOT_FOUND', 429: 'RATE_LIMITED', 503: 'SERVICE_UNAVAILABLE' };
  const string = (value) => typeof value === 'string';
  const nullable = (value, check) => value === null || check(value);
  const quantity = (value) => Number.isSafeInteger(value) && value > 0;
  const shape = (value, keys) => value !== null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key));
  const dateOnly = (value) => string(value) && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
  const timestamp = (value) => string(value) && /^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?Z$/.test(value) && dateOnly(value.slice(0, 10)) && Number.isFinite(Date.parse(value));
  const https = (value) => {
    if (!string(value) || !/^https:\/\/[^\s\\]+$/.test(value)) return false;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !!url.hostname && !url.username && !url.password;
    } catch { return false; }
  };

  function validResponse(value, status) {
    if (status !== 200) {
      return shape(value, ['apiVersion', 'ok', 'error']) && value.apiVersion === '1' && value.ok === false &&
        shape(value.error, ['code', 'message', 'retryAfterSeconds']) && !!errorCodes[status] && value.error.code === errorCodes[status] &&
        string(value.error.message) && nullable(value.error.retryAfterSeconds, quantity);
    }
    if (!shape(value, ['apiVersion', 'ok', 'fetchedAt', 'dataAsOf', 'partial', 'order', 'items', 'groups']) || !['1', '2'].includes(value.apiVersion) || value.ok !== true || !timestamp(value.fetchedAt) || typeof value.partial !== 'boolean') return false;
    if (!shape(value.dataAsOf, ['shopify', 'airtable']) || !timestamp(value.dataAsOf.shopify) || !nullable(value.dataAsOf.airtable, timestamp)) return false;
    if (!shape(value.order, ['number', 'placedAt', 'state']) || !string(value.order.number) || !timestamp(value.order.placedAt) || !['in_progress', 'complete', 'cancelled', 'update_unavailable'].includes(value.order.state)) return false;
    if (!Array.isArray(value.items) || !Array.isArray(value.groups)) return false;
    const counts = new Map();
    for (const item of value.items) {
      const itemKeys = ['key', 'title', 'variantTitle', 'imageUrl', 'quantity'];
      if (value.apiVersion === '2') itemKeys.push('deliveryLeadTime');
      if (!shape(item, itemKeys) || !string(item.key) || !item.key || counts.has(item.key) || !string(item.title) || !nullable(item.variantTitle, string) || !nullable(item.imageUrl, https) || !quantity(item.quantity)) return false;
      if (value.apiVersion === '2' && !nullable(item.deliveryLeadTime, (raw) => {
        const match = string(raw) && raw.match(/^(\d{1,3})-(\d{1,3}) (?:working )?(?:days?|weeks?)$/);
        return !!match && Number(match[1]) <= Number(match[2]) && Number(match[2]) <= 520;
      })) return false;
      counts.set(item.key, 0);
    }
    const groups = new Set();
    for (const group of value.groups) {
      if (!shape(group, ['key', 'source', 'status', 'label', 'message', 'expectedWarehouseDate', 'deliveredAt', 'tracking', 'items']) || !string(group.key) || !group.key || groups.has(group.key) || !['airtable', 'shopify', 'unavailable'].includes(group.source) || !statuses.has(group.status) || !string(group.label) || !nullable(group.message, string) || !nullable(group.expectedWarehouseDate, dateOnly) || !nullable(group.deliveredAt, timestamp) || !Array.isArray(group.tracking) || !Array.isArray(group.items) || !group.items.length) return false;
      groups.add(group.key);
      for (const track of group.tracking) {
        if (!shape(track, ['carrier', 'number', 'url']) || !nullable(track.carrier, string) || !nullable(track.number, string) || !nullable(track.url, https)) return false;
      }
      const portions = new Set();
      for (const portion of group.items) {
        if (!shape(portion, ['itemKey', 'quantity']) || !counts.has(portion.itemKey) || portions.has(portion.itemKey) || !quantity(portion.quantity)) return false;
        portions.add(portion.itemKey);
        counts.set(portion.itemKey, counts.get(portion.itemKey) + portion.quantity);
      }
    }
    if (!value.items.every((item) => counts.get(item.key) === item.quantity)) return false;
    if (value.order.state === 'complete' && (value.partial || !value.groups.length || value.groups.some((group) => group.status !== 'delivered'))) return false;
    if (value.groups.some((group) => group.status === 'update_unavailable' || group.source === 'unavailable') && !value.partial) return false;
    return true;
  }

  function lookupUrl(base, root, origin) {
    const path = base.replace(/\/+$/, '');
    if (!/^\/(?:apps|a|community|tools)\/[a-zA-Z0-9_-]+(?:\/[a-zA-Z0-9_-]+)*$/.test(path)) throw new Error('Invalid path');
    if (!/^\/(?:[a-zA-Z0-9-]+\/?)?$/.test(root)) throw new Error('Invalid locale');
    const url = new URL(`${root.replace(/\/$/, '')}${path}/lookup`, origin);
    if (url.origin !== origin) throw new Error('Invalid origin');
    return url.pathname;
  }

  class LookupError extends Error {
    constructor(kind, retryable = false, seconds = null) {
      super(kind);
      this.kind = kind;
      this.retryable = retryable;
      this.seconds = seconds;
    }
  }

  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const icon = (name) => {
    const paths = {
      box: 'M3 7 12 3l9 4v10l-9 4-9-4V7Zm0 0 9 4 9-4M12 11v10M7.5 5 17 9',
      check: 'M8 12l3 3 5-6M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
      clock: 'M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
      delivery: 'M2 6h11v9H2zM13 9h3.6L20 12.2V15h-7zM7.6 17.6a1.6 1.6 0 1 1-3.2 0 1.6 1.6 0 0 1 3.2 0M18.6 17.6a1.6 1.6 0 1 1-3.2 0 1.6 1.6 0 0 1 3.2 0',
      close: 'm8 8 8 8m0-8-8 8M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
      truck: 'M3 5h11v11H3V5Zm11 4h4l3 4v3h-7M8 17a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm12 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z'
    };
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    for (const [key, value] of Object.entries({ viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true', focusable: 'false', class: 'order-tracker__icon' })) svg.setAttribute(key, value);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', paths[name] || paths.box);
    svg.append(path);
    return svg;
  };

  function displayGroups(groups) {
    const buckets = new Map();
    for (const group of groups) {
      const identity = JSON.stringify([group.source, group.status, group.label, group.message, group.expectedWarehouseDate, group.deliveredAt, group.tracking]);
      if (!buckets.has(identity)) buckets.set(identity, { ...group, items: [] });
      buckets.get(identity).items.push(...group.items.map((portion) => ({ ...portion })));
    }
    return [...buckets.values()];
  }

  const tone = (status) => status === 'delivered' ? 'success' : ['dispatched', 'in_transit', 'out_for_delivery'].includes(status) ? 'transit' : ['on_hold', 'update_unavailable', 'awaiting_supplier', 'awaiting_collection'].includes(status) ? 'attention' : ['cancelled', 'returned_or_refunded'].includes(status) ? 'neutral' : 'progress';

  class OrderTracker extends HTMLElement {
    static validResponse = validResponse;
    static lookupUrl = lookupUrl;
    static displayGroups = displayGroups;

    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.sequence = 0;
      this.cooldownUntil = 0;
      this.copy = Object.fromEntries([...this.querySelectorAll('[data-copy]')].map((element) => [element.dataset.copy, element.textContent]));
      this.form = this.querySelector('form');
      this.lookupHeader = this.querySelector('[data-lookup-header]');
      this.toolbar = this.querySelector('[data-toolbar]');
      this.contact = this.querySelector('[data-contact]');
      this.fieldset = this.querySelector('fieldset');
      this.orderInput = this.querySelector('[data-order-number]');
      this.emailInput = this.querySelector('[data-email]');
      this.feedback = this.querySelector('[data-feedback]');
      this.results = this.querySelector('[data-results]');
      this.retryButton = this.querySelector('[data-retry]');
      this.refreshButton = this.querySelector('[data-refresh]');
      this.resetButton = this.querySelector('[data-start-again]');
      const on = (target, type, listener) => target.addEventListener(type, listener, { signal: this.lifecycle.signal });
      on(this.form, 'submit', (event) => { event.preventDefault(); this.submit(); });
      on(this.form, 'input', () => {
        this.credentials = null;
        this.retryButton.hidden = true;
        this.orderInput.removeAttribute('aria-invalid');
        this.emailInput.removeAttribute('aria-invalid');
      });
      on(this.retryButton, 'click', () => this.credentials ? this.lookup() : this.submit());
      on(this.refreshButton, 'click', () => this.lookup());
      on(this.resetButton, 'click', () => this.startAgain());
      on(window, 'pagehide', () => this.startAgain(false));
      this.startAgain(false);
    }

    disconnectedCallback() {
      this.startAgain(false);
      this.lifecycle?.abort();
      this.lifecycle = null;
      clearTimeout(this.cooldownTimer);
    }

    t(key, values = {}) {
      return (this.copy[key] || '').replace(/\[(\w+)\]/g, (match, name) => Object.hasOwn(values, name) ? values[name] : match);
    }

    date(value, withTime = false) {
      const locale = this.dataset.locale === 'en' ? 'en-GB' : this.dataset.locale || 'en-GB';
      const options = { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/London' };
      if (withTime) Object.assign(options, { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
      if (value.length === 10) options.timeZone = 'UTC';
      return new Intl.DateTimeFormat(locale, options).format(new Date(value.length === 10 ? `${value}T12:00:00Z` : value));
    }

    submit() {
      if (this.busy || Date.now() < this.cooldownUntil) return;
      const orderNumber = this.orderInput.value.trim();
      const email = this.emailInput.value.trim();
      this.emailInput.value = email;
      const orderValid = !!orderNumber && orderNumber.length <= 80;
      const emailValid = !!email && email.length <= 254 && this.emailInput.validity.valid;
      this.orderInput.setAttribute('aria-invalid', String(!orderValid));
      this.emailInput.setAttribute('aria-invalid', String(!emailValid));
      if (!orderValid || !emailValid) {
        this.dataset.state = 'error';
        this.feedback.textContent = this.t('invalid_input');
        (orderValid ? this.emailInput : this.orderInput).focus();
        return;
      }
      this.credentials = { orderNumber, email };
      this.lookup();
    }

    controls() {
      const locked = this.busy || Date.now() < this.cooldownUntil;
      this.fieldset.disabled = this.busy;
      const submit = this.form.querySelector('[type="submit"]');
      submit.disabled = locked;
      submit.setAttribute('aria-busy', String(this.busy));
      submit.querySelector('.order-tracker__spinner').hidden = !this.busy;
      this.retryButton.disabled = locked;
      this.refreshButton.disabled = locked;
      this.results.setAttribute('aria-busy', String(this.busy));
      this.contact.hidden = !this.hasResult;
    }

    cooldown(seconds) {
      this.cooldownUntil = Math.max(this.cooldownUntil, Date.now() + seconds * 1000);
      clearTimeout(this.cooldownTimer);
      const tick = () => {
        this.controls();
        if (Date.now() < this.cooldownUntil) this.cooldownTimer = setTimeout(tick, Math.min(1000, this.cooldownUntil - Date.now()));
      };
      tick();
    }

    async request(signal) {
      let url;
      try { url = lookupUrl(this.dataset.proxyBase, this.dataset.rootUrl, location.origin); }
      catch { throw new LookupError('service_unavailable'); }
      const timeout = new AbortController();
      const abort = () => timeout.abort();
      signal.addEventListener('abort', abort, { once: true });
      const timer = setTimeout(abort, 20000);
      try {
        const response = await fetch(url, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/vnd.otellos.order-tracker.v2+json, application/json' },
          body: JSON.stringify(this.credentials), credentials: 'same-origin', mode: 'same-origin',
          cache: 'no-store', redirect: 'error', referrerPolicy: 'no-referrer', signal: timeout.signal
        });
        const retryAfter = response.headers.get('Retry-After');
        const headerSeconds = /^\d+$/.test(retryAfter || '') ? Number(retryAfter) : Math.ceil((Date.parse(retryAfter) - Date.now()) / 1000);
        const hint = quantity(headerSeconds) ? headerSeconds : null;
        let payload;
        try {
          if (!/\bapplication\/json\b/i.test(response.headers.get('Content-Type') || '')) throw new Error('Not JSON');
          payload = await response.json();
        } catch {
          throw new LookupError(response.status === 429 ? 'rate_limited' : 'service_unavailable', [200, 502, 503, 504].includes(response.status), hint);
        }
        if ([502, 504].includes(response.status)) throw new LookupError('service_unavailable', true, hint);
        if (!validResponse(payload, response.status)) throw new LookupError('service_unavailable');
        if (!payload.ok) throw new LookupError({ 400: 'invalid_input', 403: 'invalid_request', 404: 'no_match', 429: 'rate_limited', 503: 'service_unavailable' }[response.status], response.status === 503, payload.error.retryAfterSeconds ?? hint);
        return payload;
      } catch (error) {
        if (signal.aborted) throw error;
        if (error instanceof LookupError) throw error;
        throw new LookupError(timeout.signal.aborted ? 'service_unavailable' : 'network_error', true);
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
      }
    }

    wait(milliseconds, signal) {
      return new Promise((resolve, reject) => {
        if (signal.aborted) { reject(new Error('Cancelled')); return; }
        const abort = () => { clearTimeout(timer); reject(new Error('Cancelled')); };
        const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, milliseconds);
        signal.addEventListener('abort', abort, { once: true });
      });
    }

    showResult(payload, focus) {
      this.render(payload);
      this.hasResult = true;
      this.dataset.hasResult = 'true';
      this.dataset.state = 'result';
      this.lookupHeader.hidden = true;
      this.form.hidden = true;
      this.feedback.textContent = this.t('found');
      this.refreshButton.hidden = false;
      this.controls();
      if (focus) this.results.querySelector('[data-result-heading]').focus();
    }

    async lookup() {
      if (this.busy || !this.credentials || Date.now() < this.cooldownUntil) return;
      const refreshing = this.hasResult;
      let resultShown = refreshing;
      const id = ++this.sequence;
      this.pending?.abort();
      this.pending = new AbortController();
      const signal = this.pending.signal;
      this.busy = true;
      this.dataset.state = 'loading';
      this.form.hidden = this.hasResult;
      this.feedback.textContent = this.t('loading');
      this.retryButton.hidden = true;
      this.refreshButton.hidden = !this.hasResult;
      this.resetButton.hidden = false;
      this.toolbar.hidden = false;
      this.controls();
      try {
        let payload;
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            payload = await this.request(signal);
            if (signal.aborted || id !== this.sequence) return;
            if (!payload.partial || attempt === 1) break;
            this.showResult(payload, !resultShown);
            resultShown = true;
            this.feedback.textContent = this.t('retrying');
            await this.wait(2000, signal);
          }
          catch (error) {
            if (signal.aborted || id !== this.sequence) return;
            if (error.seconds) this.cooldown(error.seconds);
            if (attempt === 1 || !error.retryable || error.seconds > 10) throw error;
            this.feedback.textContent = this.t('retrying');
            await this.wait(Math.max(2000, (error.seconds || 0) * 1000), signal);
          }
        }
        if (signal.aborted || id !== this.sequence) return;
        this.showResult(payload, !resultShown);
      } catch (error) {
        if (signal.aborted || id !== this.sequence) return;
        this.dataset.state = 'error';
        if (['no_match', 'invalid_input', 'invalid_request'].includes(error.kind)) {
          this.hasResult = false;
          this.dataset.hasResult = 'false';
          this.results.replaceChildren();
          this.lookupHeader.hidden = false;
          this.refreshButton.hidden = true;
        }
        this.form.hidden = this.hasResult;
        this.feedback.textContent = this.t(error.kind || 'service_unavailable');
        if (error.seconds) this.feedback.textContent += ` ${this.t('wait', { seconds: error.seconds })}`;
        if (this.hasResult) this.feedback.textContent += ` ${this.t('previous_update')}`;
        this.retryButton.hidden = this.hasResult || ['no_match', 'invalid_input', 'invalid_request'].includes(error.kind);
        this.feedback.focus();
      } finally {
        if (id === this.sequence) { this.busy = false; this.controls(); }
      }
    }

    startAgain(focus = true) {
      this.sequence++;
      this.pending?.abort();
      this.pending = null;
      this.credentials = null;
      this.busy = false;
      this.hasResult = false;
      this.dataset.hasResult = 'false';
      this.lookupHeader.hidden = false;
      this.toolbar.hidden = true;
      this.form.reset();
      this.form.querySelector('details').open = false;
      this.orderInput.value = '';
      this.emailInput.value = '';
      this.orderInput.removeAttribute('aria-invalid');
      this.emailInput.removeAttribute('aria-invalid');
      this.results.replaceChildren();
      this.feedback.textContent = '';
      this.form.hidden = false;
      this.retryButton.hidden = true;
      this.refreshButton.hidden = true;
      this.resetButton.hidden = true;
      this.dataset.state = 'initial';
      this.controls();
      if (focus) this.orderInput.focus();
    }

    render(payload) {
      const fragment = document.createDocumentFragment();
      const summary = node('div', 'order-tracker__summary');
      const orderHeading = node('div', 'order-tracker__order-heading');
      const heading = node('h1', '', this.t('order_heading', { number: payload.order.number }));
      heading.setAttribute('data-result-heading', '');
      heading.tabIndex = -1;
      orderHeading.append(heading, node('p', 'order-tracker__placed', this.t('placed_on', { date: this.date(payload.order.placedAt) })));
      summary.append(orderHeading);
      if (payload.partial) summary.append(node('p', 'order-tracker__partial', this.t('partial')));
      fragment.append(summary);
      const items = new Map(payload.items.map((item) => [item.key, item]));
      const groups = displayGroups(payload.groups);
      const compact = groups.reduce((count, group) => count + group.items.length, 0) > 1;
      const deliveries = node('div', compact ? 'order-tracker__deliveries order-tracker__deliveries--compact' : 'order-tracker__deliveries');
      if (compact) deliveries.append(node('h2', 'order-tracker__deliveries-title', this.t('your_items')));
      for (const group of groups) {
        const section = node('section', 'order-tracker__group');
        section.dataset.status = group.status;
        section.dataset.tone = tone(group.status);
        const header = node('div', 'order-tracker__group-header');
        const badge = node('span', 'order-tracker__delivery-icon');
        badge.append(icon(group.status === 'delivered' ? 'check' : ['dispatched', 'in_transit', 'out_for_delivery', 'fulfilled'].includes(group.status) ? 'truck' : ['on_hold', 'update_unavailable'].includes(group.status) ? 'clock' : group.status === 'cancelled' ? 'close' : 'box'));
        header.append(badge);
        const groupCopy = node('div', 'order-tracker__group-copy');
        groupCopy.append(node(compact ? 'h3' : 'h2', 'order-tracker__group-title', group.label));
        const hasCourierLink = group.tracking.some((tracking) => tracking.url);
        if (group.message && group.message !== group.label && !(group.expectedWarehouseDate && group.message === this.t('warehouse_label')) && !(compact && hasCourierLink && group.message === 'Tracking available.')) groupCopy.append(node('p', '', group.message));
        if (group.expectedWarehouseDate) groupCopy.append(node('p', 'order-tracker__delivery-date', this.t('warehouse_date', { date: this.date(group.expectedWarehouseDate) })));
        if (group.status === 'delivered' && group.deliveredAt) groupCopy.append(node('p', 'order-tracker__delivery-date', this.t('delivered_on', { date: this.date(group.deliveredAt) })));
        if (!compact && group.status === 'fulfilled' && hasCourierLink) groupCopy.append(node('p', '', this.t('courier_latest')));
        header.append(groupCopy);
        const progress = this.progress(group, payload.order.placedAt);
        const trackingRows = [];
        for (const tracking of group.tracking) {
          if (!tracking.carrier && !tracking.number && !tracking.url) continue;
          const row = node('div', 'order-tracker__tracking');
          const info = node('div', 'order-tracker__tracking-info');
          if (tracking.carrier) info.append(node('strong', '', tracking.carrier));
          if (tracking.url && https(tracking.url)) {
            const link = node('a', 'order-tracker__tracking-link');
            link.append(node('span', 'order-tracker__link-label', tracking.number || tracking.url));
            const arrow = node('span', 'order-tracker__action-icon', '↗');
            arrow.setAttribute('aria-hidden', 'true');
            link.append(arrow, node('span', 'order-tracker__visually-hidden', this.t('opens_new_tab')));
            if (tracking.number) link.setAttribute('aria-label', `${this.t('tracking_number', { number: tracking.number })}. ${this.t('opens_new_tab')}`);
            link.href = tracking.url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.referrerPolicy = 'no-referrer';
            info.append(link);
          } else if (tracking.number) info.append(node('span', '', this.t('tracking_number', { number: tracking.number })));
          row.append(info);
          trackingRows.push(row);
        }
        const list = node('ul', 'order-tracker__items');
        list.setAttribute('aria-label', this.t('items_heading'));
        for (const portion of group.items) {
          const item = items.get(portion.itemKey);
          const card = node('li', 'order-tracker__item');
          const media = node('div', 'order-tracker__image');
          const fallback = node('span', 'order-tracker__fallback');
          fallback.append(icon('box'), node('span', '', this.t('image_unavailable')));
          media.append(fallback);
          if (item.imageUrl && https(item.imageUrl)) {
            const img = node('img');
            img.alt = '';
            img.width = 160;
            img.height = 160;
            img.loading = 'lazy';
            img.referrerPolicy = 'no-referrer';
            img.addEventListener('load', () => { fallback.hidden = true; img.hidden = false; }, { once: true });
            img.addEventListener('error', () => { img.remove(); fallback.hidden = false; }, { once: true });
            img.src = item.imageUrl;
            media.append(img);
          }
          const detail = node('div');
          detail.append(node('h3', 'order-tracker__item-title', item.title));
          if (item.variantTitle) detail.append(node('p', 'order-tracker__variant', item.variantTitle));
          const metadata = node('div', 'order-tracker__item-meta');
          metadata.append(node('p', '', this.t('quantity', { quantity: portion.quantity })), node('span', 'order-tracker__visually-hidden', group.label));
          detail.append(metadata);
          const estimate = item.deliveryLeadTime && window.YvDeliveryEstimate?.computeRange(item.deliveryLeadTime, payload.order.placedAt);
          const delivery = node('p', 'order-tracker__item-delivery');
          const deliveryText = node('span', 'order-tracker__item-delivery-text', this.t(estimate ? 'item_delivery_estimate' : 'item_delivery_unavailable'));
          if (estimate) {
            const deliveryIcon = node('span', 'order-tracker__item-delivery-icon');
            deliveryIcon.append(icon('delivery'));
            delivery.append(deliveryIcon);
            deliveryText.append(' ', node('strong', '', estimate));
          }
          delivery.append(deliveryText);
          detail.append(delivery);
          card.append(media, detail);
          list.append(card);
        }
        if (compact) {
          const body = node('div', 'order-tracker__group-body');
          const update = node('div', 'order-tracker__item-update');
          const journey = node('details', 'order-tracker__journey');
          const disclosure = node('summary');
          disclosure.append(node('span', 'order-tracker__link-label', this.t('view_progress')));
          journey.append(disclosure, progress);
          if (group.status === 'fulfilled' && hasCourierLink) journey.append(node('p', 'order-tracker__courier-note', this.t('courier_latest')));
          if (trackingRows.length) update.append(node('p', 'order-tracker__tracking-heading', this.t('track_delivery')));
          update.append(...trackingRows, journey);
          body.append(list, update);
          section.append(header, body);
        } else section.append(header, ...trackingRows, progress, list);
        deliveries.append(section);
      }
      fragment.append(deliveries);
      const purchase = node('section', 'order-tracker__purchase');
      purchase.append(node('h2', '', this.t('purchase_information')));
      const facts = node('dl', 'order-tracker__purchase-facts');
      for (const [key, value] of [
        ['summary_order_number', payload.order.number],
        ['summary_order_date', this.date(payload.order.placedAt)],
        ['summary_items', payload.items.length],
        ['summary_quantity', payload.items.reduce((sum, item) => sum + item.quantity, 0)],
        ['summary_status', this.t(payload.order.state)]
      ]) {
        const row = node('div');
        row.append(node('dt', '', this.t(key)), node('dd', key === 'summary_status' ? 'order-tracker__overall' : '', String(value)));
        facts.append(row);
      }
      purchase.append(facts);
      fragment.append(purchase);
      const freshness = node('details', 'order-tracker__freshness');
      const disclosure = node('summary');
      disclosure.append(node('span', 'order-tracker__link-label', this.t('update_details')));
      freshness.append(disclosure);
      freshness.append(node('p', '', this.t('order_checked', { date: this.date(payload.dataAsOf.shopify, true) })));
      if (payload.dataAsOf.airtable) freshness.append(node('p', '', this.t('progress_checked', { date: this.date(payload.dataAsOf.airtable, true) })));
      freshness.append(node('p', '', this.t('refreshed_at', { date: this.date(payload.fetchedAt, true) })));
      fragment.append(freshness);
      this.results.replaceChildren(fragment);
    }

    progress(group, placedAt) {
      const list = node('ol', 'order-tracker__progress');
      list.setAttribute('aria-label', this.t('progress_heading'));
      const step = (label, state, message) => {
        const item = node('li', `order-tracker__step order-tracker__step--${state}`);
        const mark = node('span', 'order-tracker__step-mark');
        mark.setAttribute('aria-hidden', 'true');
        if (state === 'complete') mark.append(icon('check'));
        const copy = node('div');
        copy.append(node('strong', '', label));
        if (message) copy.append(node('p', '', message));
        item.append(mark, copy);
        if (state === 'current' || (state === 'complete' && group.status === 'delivered' && label === group.label)) item.setAttribute('aria-current', 'step');
        list.append(item);
        return copy;
      };
      if (group.status !== 'received') step(this.t('order_placed'), 'complete', this.date(placedAt));
      const current = step(group.label, group.status === 'delivered' ? 'complete' : 'current', group.status === 'received' ? this.date(placedAt) : null);
      if (group.status === 'delivered' && group.deliveredAt) current.append(node('p', '', this.t('delivered_on', { date: this.date(group.deliveredAt) })));
      return list;
    }
  }

  customElements.define('order-tracker', OrderTracker);
})();
